/**
 * Sign in / register / reset — rebuilt to Vault Sign In.dc.html. A left-aligned
 * split (brand + three sentences on the left, form card on the right; the aside
 * drops on phone and the card carries the lockup). Every state is drawn: the
 * default sign-in, a wrong-password error with attempts remaining, the busy
 * "Signing you in…" spinner, the too-many-attempts lock, and the three
 * password-recovery screens (request → sent → set a new one). Social first,
 * then email, with the last-used provider marked. Quoted copy is verbatim.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, tokens } from '../api.js';
import { useAuth } from '../App.jsx';

const ASIDE_POINTS = [
  'One card, R59 a month, and a 5-day trial before anything is charged.',
  'Web, phone and watch — logging offline and syncing when you are back.',
  'We never ask for your online-banking login. You import your own statements.',
];

export default function AuthPage({ mode = 'login' }) {
  const isRegister = mode === 'register';
  const isReset = mode === 'reset';
  const { reload } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();

  // login mode cycles signin → forgot → sent in place; reset/register are fixed.
  const [view, setView] = useState(isReset ? 'reset' : isRegister ? 'register' : 'signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [consent, setConsent] = useState(false);
  const [remember, setRemember] = useState(true);
  const [reveal, setReveal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState(null);   // {icon, title, body, tone}
  const [pwError, setPwError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [resendIn, setResendIn] = useState(0);
  const lastProvider = (() => { try { return localStorage.getItem('vault_last_provider'); } catch { return null; } })();

  const [invite, setInvite] = useState(() => {
    try { return sessionStorage.getItem('vault_ref') || ''; } catch { return ''; }
  });
  const refBody = () => (invite.trim() ? { ref: invite.trim().toUpperCase() } : {});

  // Resend cooldown ticker (SENT screen).
  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setTimeout(() => setResendIn((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const finish = async (data) => {
    tokens.set(data, remember);
    try { sessionStorage.removeItem('vault_ref'); } catch { /* ignore */ }
    await reload();
    nav('/app');
  };

  const clearErrors = () => { setBanner(null); setPwError(''); setEmailError(''); };

  const doLogin = async () => {
    setBusy(true); clearErrors();
    try {
      const data = await api('/auth/token/', { method: 'POST', body: { username: email, password } });
      await finish(data);
    } catch (err) {
      const d = err.data || {};
      if (d.locked) {
        const at = d.locked_until ? new Date(d.locked_until * 1000).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' }) : 'shortly';
        setView('locked');
        setBanner({ icon: '🔒', title: `Try again at ${at}`,
          body: 'Nothing is wrong with your account — this only protects it from someone guessing. A reset link works right now if you would rather not wait.', tone: 'warn' });
      } else {
        const left = typeof d.attempts_remaining === 'number' ? d.attempts_remaining : null;
        setPwError(left != null
          ? `That password does not match this account. ${left} attempt${left === 1 ? '' : 's'} left before we pause sign-in for fifteen minutes.`
          : (d.detail || 'That password does not match this account.'));
        setBanner({ icon: '⚠', title: 'That did not work',
          body: 'Check the password, or send yourself a reset link — it takes about a minute.', tone: 'warn' });
      }
    } finally { setBusy(false); }
  };

  const doRegister = async () => {
    setBusy(true); clearErrors();
    try {
      const data = await api('/auth/register/', { method: 'POST', body: { email, password, consent, ...refBody() } });
      await finish(data);
    } catch (err) {
      const d = err.data || {};
      setEmailError(d.email?.[0] || '');
      setPwError(d.password?.[0] || '');
      if (d.consent?.[0] || d.detail) setBanner({ icon: '⚠', title: 'Check the form', body: d.consent?.[0] || d.detail, tone: 'warn' });
    } finally { setBusy(false); }
  };

  const doForgot = async () => {
    setBusy(true); clearErrors();
    try {
      await api('/auth/password/reset/', { method: 'POST', body: { email } });
    } catch { /* the endpoint never fails loudly — same message either way */ }
    setView('sent');
    setResendIn(60);
    setBusy(false);
  };

  const doReset = async () => {
    setBusy(true); clearErrors();
    try {
      const data = await api('/auth/password/reset/confirm/', {
        method: 'POST', body: { uid: params.get('uid'), token: params.get('token'), password } });
      await finish(data);
    } catch (err) {
      const d = err.data || {};
      if (d.password?.[0]) setPwError(d.password[0]);
      else setBanner({ icon: '⚠', title: 'That link did not work', body: d.detail || 'This reset link is invalid or has expired. Ask for a new one.', tone: 'warn' });
    } finally { setBusy(false); }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (view === 'signin') doLogin();
    else if (view === 'register') doRegister();
    else if (view === 'forgot') doForgot();
    else if (view === 'reset') doReset();
    else if (view === 'locked') { setView('forgot'); clearErrors(); }
    else if (view === 'sent') { window.location.href = 'mailto:'; }
  };

  // Exchange a verified provider id_token for a Vault session. Google delivers
  // its token via the rendered-button callback below; Apple via getProviderIdToken().
  const submitProviderToken = async (provider, idToken) => {
    setBusy(true); clearErrors();
    try {
      const data = await api(`/auth/${provider}/`, { method: 'POST', body: { id_token: idToken, consent: true, ...refBody() } });
      try { localStorage.setItem('vault_last_provider', provider); } catch { /* ignore */ }
      await finish(data);
    } catch (err) {
      setBanner({ icon: '⚠', title: 'Could not sign in', body: err.message || `Could not sign in with ${provider}.`, tone: 'warn' });
    } finally { setBusy(false); }
  };

  // Apple keeps the click -> popup -> token path; Google uses its own button.
  const social = async (provider) => {
    clearErrors();
    let idToken;
    try {
      setBusy(true);
      idToken = await getProviderIdToken(provider);
    } catch (err) {
      setBanner({ icon: '⚠', title: 'Could not sign in', body: err.message || `Could not sign in with ${provider}.`, tone: 'warn' });
      setBusy(false);
      return;
    }
    await submitProviderToken(provider, idToken);
  };

  // ── per-view copy ──
  const CFG = {
    signin: { title: 'Welcome back', sub: 'Pick up where your month left off.',
              social: true, email: true, password: true, forgot: true, keep: true,
              submit: 'Sign in', foot: ['New here?', 'Start free — 5 days', '/register'] },
    register: { title: 'Create your Vault', sub: 'Five free days, then R59 a month. Cancel anytime.',
                social: true, email: true, password: true, keep: true,
                submit: invite.trim() ? 'Start my trial + bonus days' : 'Start my 5-day trial',
                foot: ['Already have a vault?', 'Sign in', '/login'] },
    forgot: { title: 'Reset your password', sub: 'Tell us the email on the account and we will send a link that works for one hour.',
              email: true, back: true, submit: 'Send me a reset link',
              foot: ['Remembered it?', 'Sign in instead', '/login'] },
    sent: { title: 'Check your email', sub: `If an account exists for ${email || 'that address'}, a reset link is on its way. It expires in an hour.`,
            back: true, submit: 'Open my email app', resend: true,
            foot: ['Wrong address?', 'Start over', '/login'] },
    reset: { title: 'Set a new password', sub: 'Choose something you have not used elsewhere. We will sign you in straight after.',
             password: true, strength: true, pwLabel: 'New password', submit: 'Save it and sign me in',
             foot: ['Changed your mind?', 'Back to sign in', '/login'] },
    locked: { title: 'Sign-in paused', sub: 'Five wrong attempts in a row, so we have paused this account for fifteen minutes to keep it safe.',
              back: true, submit: 'Send me a reset link instead',
              foot: ['Still stuck?', 'Message support', '/account'] },
  };
  const cfg = CFG[view];
  const score = Math.min(4, Math.floor(password.length / 3));
  const strengthLine = score <= 1 ? 'Too short — twelve characters or more is the easiest way to make it strong.'
    : score === 2 ? 'Getting there. Length helps more than symbols do.'
    : score === 3 ? 'Good. A few more characters and it is strong.'
    : 'Strong. Nothing else needed.';
  const barColor = (i) => (i < score ? (score <= 1 ? 'bg-red-400' : score === 2 ? 'bg-amber-400' : 'bg-vpurple') : 'bg-edge');

  const Social = ({ provider, label, mark }) => (
    <button type="button" onClick={() => social(provider)} disabled={busy}
            className={`flex items-center gap-3 w-full min-h-[50px] px-4 rounded-xl border text-sm font-medium transition hover:bg-edge/40 ${lastProvider === provider ? 'border-vpurple' : 'border-edge'}`}>
      <span className="w-6 h-6 shrink-0 rounded grid place-items-center text-[13px] font-semibold bg-white/5 text-slate-200">{mark}</span>
      <span className="flex-1 text-left">{label}</span>
      {lastProvider === provider && <span className="text-[10px] tracking-wide px-2 py-0.5 rounded border border-vpurple/50 text-vpurple">last used</span>}
    </button>
  );

  return (
    <div className="min-h-full px-6 py-10 md:py-16">
      <div className="max-w-5xl mx-auto grid gap-10 lg:gap-16 md:grid-cols-[1fr_minmax(0,26rem)] items-center">
        <aside className="hidden md:flex flex-col gap-4 min-w-0">
          <Link to="/" className="flex items-center gap-3"><img src="/logo.svg" alt="" className="w-9 h-9 rounded-lg" /><span className="text-2xl font-semibold">Vault</span></Link>
          <h1 className="text-4xl font-semibold leading-[1.1] tracking-tight mt-1">Know what's left,<br /><span className="text-vpurple">in every bank.</span></h1>
          <p className="text-sm text-slate-400 leading-relaxed max-w-sm">Payday-first budgeting. Your month starts the day you are paid, and every account carries its own honest leftover.</p>
          <ul className="flex flex-col gap-3 mt-1">
            {ASIDE_POINTS.map((p) => (
              <li key={p} className="flex gap-2.5 items-start text-[13px] text-slate-500 leading-relaxed"><span className="w-1.5 h-1.5 rounded-full bg-vpurple mt-[7px] shrink-0" />{p}</li>
            ))}
          </ul>
        </aside>

        <div className="flex flex-col gap-4 min-w-0 w-full">
          <form onSubmit={onSubmit} className="card flex flex-col gap-4">
            <Link to="/" className="md:hidden flex items-center gap-2 justify-center mb-1"><img src="/logo.svg" alt="" className="w-8 h-8 rounded-lg" /><span className="text-xl font-semibold">Vault</span></Link>

            <div>
              <h2 className="text-2xl font-semibold tracking-tight">{cfg.title}</h2>
              <p className="text-sm text-slate-400 mt-1 leading-relaxed">{cfg.sub}</p>
            </div>

            {banner && (
              <div className={`flex gap-3 items-start px-4 py-3 rounded-xl border ${banner.tone === 'warn' ? 'border-amber-400/60 bg-amber-400/[0.08]' : 'border-vpurple/50 bg-vpurple/10'}`}>
                <div className="text-sm shrink-0">{banner.icon}</div>
                <div className="min-w-0">
                  <div className={`text-[13px] font-semibold ${banner.tone === 'warn' ? 'text-amber-400' : 'text-vpurple'}`}>{banner.title}</div>
                  <div className="text-[12.5px] leading-relaxed mt-0.5 text-slate-400">{banner.body}</div>
                </div>
              </div>
            )}

            {cfg.social && (GOOGLE_CLIENT_ID || APPLE_CLIENT_ID) && <>
              <div className="flex flex-col gap-2.5">
                {GOOGLE_CLIENT_ID && <GoogleSignInButton onCredential={(cred) => submitProviderToken('google', cred)} />}
                {APPLE_CLIENT_ID && <Social provider="apple" label="Continue with Apple" mark="" />}
              </div>
              <div className="flex items-center gap-3 text-slate-500 text-xs"><div className="flex-1 h-px bg-edge" />or with email<div className="flex-1 h-px bg-edge" /></div>
            </>}

            {cfg.email && (
              <div>
                <label className="block text-[12px] text-slate-400 mb-1.5">Email{view === 'signin' ? ' or username' : ''}</label>
                <input className="input" type={view === 'signin' ? 'text' : 'email'} required={view !== 'signin'}
                       placeholder={view === 'signin' ? 'you@example.com or username' : 'you@example.com'}
                       value={email} onChange={(e) => setEmail(e.target.value)}
                       autoComplete={view === 'signin' ? 'username' : 'email'}
                       readOnly={view === 'sent'} />
                {emailError && <div className="text-[12px] text-red-400 mt-1.5 leading-relaxed">{emailError}</div>}
              </div>
            )}

            {cfg.password && (
              <div>
                <div className="flex items-baseline gap-2">
                  <label className="block text-[12px] text-slate-400 mb-1.5 mr-auto">{cfg.pwLabel || 'Password'}</label>
                  {cfg.forgot && <button type="button" className="text-[12.5px] font-medium text-vpurple" onClick={() => { setView('forgot'); clearErrors(); }}>Forgot it?</button>}
                </div>
                <div className="flex gap-2 items-stretch">
                  <input className="input flex-1 min-w-0" type={reveal ? 'text' : 'password'} required placeholder="••••••••"
                         value={password} onChange={(e) => setPassword(e.target.value)}
                         autoComplete={view === 'reset' || view === 'register' ? 'new-password' : 'current-password'} />
                  <button type="button" onClick={() => setReveal((r) => !r)} title={reveal ? 'Hide password' : 'Show password'}
                          className="w-12 shrink-0 rounded-xl border border-edge text-slate-400 hover:text-slate-200 transition">{reveal ? '🙈' : '👁'}</button>
                </div>
                {pwError && <div className="text-[12px] text-red-400 mt-1.5 leading-relaxed">{pwError}</div>}
                {cfg.strength && (
                  <div className="mt-2.5">
                    <div className="flex gap-1">
                      {[0, 1, 2, 3].map((i) => <div key={i} className={`flex-1 h-1 rounded-full ${barColor(i)}`} />)}
                    </div>
                    <div className="text-[12px] text-slate-500 mt-1.5 leading-relaxed">{strengthLine}</div>
                  </div>
                )}
              </div>
            )}

            {view === 'register' && (
              <div>
                <label className="block text-[12px] text-slate-400 mb-1.5">Invite code (optional)</label>
                <input className="input" placeholder="ABCD1234" value={invite}
                       onChange={(e) => setInvite(e.target.value)} autoCapitalize="characters" autoComplete="off" />
                {invite.trim() && <p className="text-xs text-emerald-400 mt-1.5">Nice — you'll get bonus welcome days added to your free trial.</p>}
              </div>
            )}

            {view === 'register' && (
              <label className="flex items-start gap-2 text-sm text-slate-400">
                <input type="checkbox" checked={consent} required onChange={(e) => setConsent(e.target.checked)} className="mt-1 accent-vpurple" />
                <span className="leading-relaxed">I consent to Vault processing my data as described in the{' '}
                  <Link to="/privacy" className="text-vpurple underline">privacy policy</Link> (POPIA). Export or delete it any time.</span>
              </label>
            )}

            {cfg.keep && (
              <label className="flex items-start gap-2 text-[13px] text-slate-400">
                <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="mt-0.5 accent-vpurple" />
                <span className="leading-relaxed">Keep me signed in on this device<span className="block text-[11.5px] text-slate-500 mt-0.5">Leave this off on a shared or public computer.</span></span>
              </label>
            )}

            <button type="submit" disabled={busy}
                    className="flex items-center justify-center gap-2.5 w-full min-h-[52px] rounded-xl border border-vpurple text-vpurple font-semibold transition hover:bg-vpurple/10 disabled:opacity-80"
                    style={busy ? { cursor: 'progress', background: 'color-mix(in srgb, var(--color-accent, #9184d9) 10%, transparent)' } : undefined}>
              {busy && <span className="w-[15px] h-[15px] rounded-full border-2 border-vpurple/30 border-t-vpurple animate-spin" />}
              <span>{busy && view === 'signin' ? 'Signing you in…' : busy && view === 'reset' ? 'Saving…' : cfg.submit}</span>
            </button>

            {cfg.resend && (
              <div className="flex flex-col gap-2.5">
                <button type="button" disabled={resendIn > 0 || busy} onClick={doForgot}
                        className="w-full min-h-[44px] rounded-xl border border-edge text-slate-200 text-[13.5px] font-medium transition hover:border-slate-500 disabled:opacity-60">Send it again</button>
                <div className="text-[12px] text-slate-500 text-center leading-relaxed">
                  {resendIn > 0 ? `You can send another in ${resendIn} second${resendIn === 1 ? '' : 's'}.` : 'Not there? Look in spam, and check the address had no typo. The link only works once.'}
                </div>
              </div>
            )}

            {cfg.back && (
              <button type="button" onClick={() => { setView(isRegister ? 'register' : 'signin'); clearErrors(); }}
                      className="w-full min-h-[44px] rounded-xl border border-edge text-slate-200 text-[13.5px] font-medium transition hover:border-slate-500">‹ Back to sign in</button>
            )}
          </form>

          <div className="flex flex-col gap-2 items-center md:items-start text-center md:text-left">
            <div className="text-[13px] text-slate-500">{cfg.foot[0]} <Link to={cfg.foot[2]} className="text-vpurple font-medium">{cfg.foot[1]}</Link></div>
            <div className="text-[11.5px] text-slate-600 leading-relaxed max-w-sm">Vault never asks for your online-banking login. <Link to="/privacy" className="hover:text-slate-400">Privacy (POPIA)</Link> · <Link to="/legal" className="hover:text-slate-400">Terms</Link></div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Provider SDKs — fully wired; they activate the moment the client IDs exist.
 * Build-time env (frontend/.env.production):
 *   VITE_GOOGLE_CLIENT_ID = the Web client ID from Google Cloud Console
 *   VITE_APPLE_CLIENT_ID  = the Services ID from Apple Developer
 */
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';
const APPLE_CLIENT_ID = import.meta.env.VITE_APPLE_CLIENT_ID || '';

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve();
    const s = Object.assign(document.createElement('script'),
                            { src, async: true, onload: resolve,
                              onerror: () => reject(new Error(`Could not load ${src}`)) });
    document.head.appendChild(s);
  });
}

/**
 * GoogleSignInButton — Google's own rendered sign-in button. Unlike One Tap
 * (google.accounts.id.prompt), a rendered button always opens a real popup on
 * click and is never suppressed by third-party-cookie or cooldown rules, while
 * still returning an id_token to the callback for the /auth/google/ exchange.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
function GoogleSignInButton({ onCredential }) {
  const holder = useRef(null);
  const cb = useRef(onCredential);
  cb.current = onCredential;
  useEffect(() => {
    let dead = false;
    loadScript('https://accounts.google.com/gsi/client').then(() => {
      if (dead || !window.google?.accounts?.id || !holder.current) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: (resp) => resp?.credential && cb.current(resp.credential),
        use_fedcm_for_prompt: true,
      });
      holder.current.innerHTML = '';
      window.google.accounts.id.renderButton(holder.current, {
        type: 'standard', theme: 'filled_black', size: 'large',
        text: 'continue_with', shape: 'pill', logo_alignment: 'center', width: 320,
      });
    }).catch(() => { /* script blocked; button stays empty, email login still works */ });
    return () => { dead = true; };
  }, []);
  return <div ref={holder} className="w-full flex justify-center min-h-[50px]" />;
}

async function getProviderIdToken(provider) {
  if (provider === 'google') {
    if (!GOOGLE_CLIENT_ID) throw new Error('Google sign-in is not configured yet.');
    await loadScript('https://accounts.google.com/gsi/client');
    return new Promise((resolve, reject) => {
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: (resp) => (resp?.credential
          ? resolve(resp.credential)
          : reject(new Error('Google sign-in was cancelled.'))),
      });
      window.google.accounts.id.prompt((n) => {
        if (n.isNotDisplayed?.() || n.isSkippedMoment?.()) {
          reject(new Error('Google sign-in popup was blocked — allow popups and try again.'));
        }
      });
    });
  }
  if (!APPLE_CLIENT_ID) throw new Error('Apple sign-in is not configured yet.');
  await loadScript('https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js');
  window.AppleID.auth.init({
    clientId: APPLE_CLIENT_ID,
    scope: 'name email',
    redirectURI: `${window.location.origin}/auth/apple/callback`,
    usePopup: true,
  });
  let res;
  try {
    res = await window.AppleID.auth.signIn();
  } catch (e) {
    // Apple rejects with {error: '<code>'} and no message, which used to show as
    // the bare "Could not sign in with apple." Name what happened instead.
    const code = e?.error || '';
    if (code === 'user_cancelled_authorize' || code === 'popup_closed_by_user') {
      throw new Error('The Apple window was closed before sign-in finished. Tap Continue with Apple to try again.');
    }
    throw new Error(code ? `Apple sign-in did not finish (${code}). Please try again.`
                         : 'Apple sign-in did not finish. Please try again.');
  }
  const token = res?.authorization?.id_token;
  if (!token) throw new Error('Apple sign-in was cancelled.');
  return token;
}
